import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Plus, Trash2, UserRound } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import GraficoDeEvolucion from './GraficoDeEvolucion';
import SemaforoDePaciente, { desdeCuando } from './SemaforoDePaciente';
import BonosDePaciente from './BonosDePaciente';
import {
  traerCatalogo, traerPacientes, guardarPaciente, borrarPaciente,
  traerCheckins, guardarCheckin, traerNotas, guardarNota, borrarNota,
  marcarAlertaVista,
} from '../lib/pacientes';

/**
 * Seguimiento de pacientes: la ficha, los check-ins y las notas de sesión.
 *
 * TRES PESTAÑAS, EN ESTE ORDEN, por la misma lógica que SeccionWhatsApp: primero
 * lo que se mira seguido. Evolución es a lo que se entra en cada sesión;
 * Check-ins, cuando hay que cargar uno a mano; Ficha, una vez al principio.
 *
 * POR QUÉ LOS DESPLEGABLES SON `<select>` NATIVOS Y NO EL Select de shadcn
 * El de shadcn es Radix, y Radix posiciona su panel flotante con `transform`.
 * Adentro del panel rige `.dashboard-container * { transform: none !important }`
 * (Dashboard.css), así que el desplegable no quedaría corrido: quedaría pegado
 * arriba a la izquierda de la pantalla. Las escapatorias declaradas son dos
 * —`[data-wa-anima]` y `[data-chart]`— y ninguna es para esto. Un `<select>`
 * nativo lo pinta el sistema operativo, no cae bajo esa regla, y encima ya está
 * teñido con el color de marca por el `accent-color` de index.css.
 */

/** El estado inicial del formulario de alta. */
const FICHA_VACIA = {
  telefono: '', nombre: '', email: '', objetivo: '',
  frecuenciaDias: 7, consienteWhatsApp: false,
};

export default function SeccionPacientes({ currentUser }) {
  const [pilares, setPilares] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [elegido, setElegido] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const [checkins, setCheckins] = useState([]);
  const [notas, setNotas] = useState([]);

  const [conAlerta, setConAlerta] = useState(0);
  const [alta, setAlta] = useState(null);      // null = el formulario está cerrado
  const [valores, setValores] = useState({});  // el check-in que se está cargando
  // Aparte de `valores` a propósito: ese mapa es de números y lo recorren el
  // promedio y el gráfico. Ver la nota de validarCheckin en backend/src/pilares.js.
  const [comentarios, setComentarios] = useState({});
  const [notaNueva, setNotaNueva] = useState('');

  // ── Carga inicial: el catálogo y la lista ──────────────────────────────────
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [cat, lista] = await Promise.all([
          traerCatalogo(currentUser),
          traerPacientes(currentUser),
        ]);
        if (!vivo) return;
        setPilares(cat.pilares || []);
        setPacientes(lista.pacientes || []);
        setConAlerta(lista.conAlerta || 0);
      } catch (e) {
        if (vivo) setError(e.message);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [currentUser]);

  // ── Lo del paciente elegido ────────────────────────────────────────────────
  const cargarDelPaciente = useCallback(async (telefono) => {
    if (!telefono) { setCheckins([]); setNotas([]); return; }
    try {
      // En paralelo y no encadenados: son dos rutas distintas justamente porque
      // las notas no viajan adentro del paciente.
      const [c, n] = await Promise.all([
        traerCheckins(currentUser, telefono),
        traerNotas(currentUser, telefono),
      ]);
      setCheckins(c.checkins || []);
      setNotas(n.notas || []);
    } catch (e) {
      setError(e.message);
    }
  }, [currentUser]);

  useEffect(() => { cargarDelPaciente(elegido?.telefono); }, [elegido, cargarDelPaciente]);

  const refrescarLista = async (seleccionar) => {
    const lista = await traerPacientes(currentUser);
    setPacientes(lista.pacientes || []);
    setConAlerta(lista.conAlerta || 0);
    if (seleccionar) {
      setElegido(lista.pacientes.find((p) => p.telefono === seleccionar) || null);
    }
  };

  const onGuardarFicha = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const { paciente } = await guardarPaciente(currentUser, alta);
      setAlta(null);
      await refrescarLista(paciente.telefono);
    } catch (err) {
      setError(err.message);
    }
  };

  const onCargarCheckin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await guardarCheckin(currentUser, elegido.telefono, { valores, comentarios });
      setValores({});
      setComentarios({});
      await cargarDelPaciente(elegido.telefono);
    } catch (err) {
      setError(err.message);
    }
  };

  if (cargando) return <p className="text-sm text-neutral-500">Cargando…</p>;

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      {/* El aviso va arriba y no adentro de la lista: es lo que hace que no
          haga falta ENTRAR a la sección para enterarse. El número lo cuenta el
          servidor — contarlo acá sería una segunda definición de "hay que
          avisar", y esas dos se separan. */}
      {conAlerta > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            {conAlerta === 1
              ? 'Hay 1 paciente que hace tiempo no completa su check-in.'
              : `Hay ${conAlerta} pacientes que hace tiempo no completan su check-in.`}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* ── La lista ──────────────────────────────────────────────────── */}
        <Card className="w-full shrink-0 p-3 lg:w-[280px]">
          <div className="mb-2 flex items-center justify-between gap-2">
            <strong className="text-sm font-semibold text-neutral-900">
              Pacientes ({pacientes.length})
            </strong>
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => { setAlta({ ...FICHA_VACIA }); setElegido(null); }}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Nuevo
            </Button>
          </div>

          {pacientes.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-neutral-500">
              Todavía no cargaste a nadie.
            </p>
          ) : (
            <ul className="flex list-none flex-col gap-1 p-0">
              {pacientes.map((p) => (
                <li key={p.telefono}>
                  <button
                    type="button"
                    onClick={() => { setAlta(null); setElegido(p); }}
                    aria-current={elegido?.telefono === p.telefono ? 'true' : undefined}
                    className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                      elegido?.telefono === p.telefono
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent hover:bg-neutral-50'
                    }`}
                  >
                    <UserRound className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[0.9rem] font-semibold text-neutral-900">
                        {p.nombre}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <SemaforoDePaciente estado={p.estado} compacto />
                        <span className="truncate text-[0.7rem] text-neutral-400">
                          {desdeCuando(p)}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* ── El detalle ────────────────────────────────────────────────── */}
        <Card className="min-w-0 flex-1 p-4">
          {alta ? (
            <form onSubmit={onGuardarFicha} className="flex flex-col gap-3">
              <h3 className="m-0 text-base font-bold text-neutral-900">Nuevo paciente</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                  Nombre y apellido *
                  <Input
                    value={alta.nombre}
                    onChange={(e) => setAlta({ ...alta, nombre: e.target.value })}
                    placeholder="Ej: Carla Domínguez"
                    required
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  WhatsApp *
                  <Input
                    value={alta.telefono}
                    onChange={(e) => setAlta({ ...alta, telefono: e.target.value })}
                    placeholder="011 15 3003-2002"
                    required
                  />
                  {/* Se aclara acá y no después de un error: el teléfono es la
                      clave de la ficha, y descubrir eso recién cuando el
                      servidor rechaza es tarde. */}
                  <small className="text-[0.7rem] text-neutral-500">
                    Es lo que identifica al paciente. Escribilo como quieras.
                  </small>
                </label>
              </div>
              <label className="flex flex-col gap-1 text-sm">
                Objetivo del proceso
                <Textarea
                  rows={2}
                  value={alta.objetivo}
                  onChange={(e) => setAlta({ ...alta, objetivo: e.target.value })}
                  placeholder="Ej: bajar el estrés y volver a entrenar tres veces por semana."
                />
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={alta.consienteWhatsApp}
                  onChange={(e) => setAlta({ ...alta, consienteWhatsApp: e.target.checked })}
                />
                <span>
                  Acepta recibir los check-ins por WhatsApp
                  <small className="block text-[0.7rem] text-neutral-500">
                    Sin esto no se le escribe. El check-in pregunta por sueño, estrés y
                    consumo de sustancias.
                  </small>
                </span>
              </label>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAlta(null)}>
                  Cancelar
                </Button>
                <Button type="submit">Guardar</Button>
              </div>
            </form>
          ) : !elegido ? (
            <p className="py-12 text-center text-sm text-neutral-500">
              Elegí un paciente de la lista, o cargá uno nuevo.
            </p>
          ) : (
            <>
              <header className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="m-0 text-base font-bold text-neutral-900">{elegido.nombre}</h3>
                  <p className="m-0 text-[0.75rem] text-neutral-500">{elegido.telefono}</p>
                  {elegido.objetivo && (
                    <p className="m-0 mt-1 max-w-prose text-sm text-neutral-600">{elegido.objetivo}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <SemaforoDePaciente estado={elegido.estado} />
                  {elegido.alerta && (
                    <Button
                      type="button" size="sm" variant="outline" className="h-8"
                      onClick={async () => {
                        await marcarAlertaVista(currentUser, elegido.telefono);
                        await refrescarLista(elegido.telefono);
                      }}
                    >
                      Ya lo contacté
                    </Button>
                  )}
                  <Badge variant="secondary">{checkins.length} check-ins</Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                    aria-label={`Borrar a ${elegido.nombre}`}
                    onClick={async () => {
                      if (!window.confirm(`¿Borrar la ficha de ${elegido.nombre} y todo su seguimiento?`)) return;
                      await borrarPaciente(currentUser, elegido.telefono);
                      setElegido(null);
                      await refrescarLista();
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </header>

              <Tabs defaultValue="evolucion">
                <TabsList>
                  <TabsTrigger value="evolucion">Evolución</TabsTrigger>
                  <TabsTrigger value="checkins">Check-ins</TabsTrigger>
                  <TabsTrigger value="bonos">Bonos</TabsTrigger>
                  <TabsTrigger value="notas">Notas</TabsTrigger>
                </TabsList>

                <TabsContent value="bonos" className="pt-3">
                  <BonosDePaciente
                    currentUser={currentUser}
                    telefono={elegido.telefono}
                    onError={setError}
                  />
                </TabsContent>

                {/* ── Evolución ──────────────────────────────────────────── */}
                <TabsContent value="evolucion" className="pt-3">
                  {/* Las notas viajan al gráfico desde acá y no por una ruta
                      propia: ya están cargadas —`traerNotas` corre en paralelo
                      con `traerCheckins`— así que pedirlas de nuevo sería un
                      viaje al servidor por un dato que está en memoria. */}
                  <GraficoDeEvolucion
                    checkins={checkins}
                    pilares={pilares}
                    notas={notas}
                    // El umbral de hueco es relativo a la frecuencia de ESTE
                    // paciente: tres semanas sin datos es abandono para un
                    // semanal y es lo normal para un mensual.
                    frecuenciaDias={elegido.frecuenciaDias}
                  />
                </TabsContent>

                {/* ── Carga manual de un check-in ────────────────────────── */}
                <TabsContent value="checkins" className="pt-3">
                  <form onSubmit={onCargarCheckin} className="flex flex-col gap-3">
                    <p className="m-0 text-sm text-neutral-600">
                      Del 1 al 10. Los que dejes vacíos no se guardan.
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {pilares.map((p) => (
                        <div key={p.id} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <label htmlFor={`valor-${p.id}`} className="min-w-0">
                              <span className="block font-semibold text-neutral-900">{p.nombre}</span>
                              <span className="block truncate text-[0.7rem] text-neutral-500">{p.pregunta}</span>
                            </label>
                            <select
                              id={`valor-${p.id}`}
                              value={valores[p.id] ?? ''}
                              onChange={(e) => setValores({
                                ...valores,
                                [p.id]: e.target.value === '' ? '' : Number(e.target.value),
                              })}
                              className="shrink-0 rounded-md border border-neutral-300 px-2 py-1 text-sm"
                            >
                              <option value="">—</option>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>

                          {/* El comentario va plegado. Seis textareas abiertas
                              convierten una carga de veinte segundos en una
                              pantalla que intimida, y el comentario es opcional
                              justamente porque casi siempre no hace falta. */}
                          {comentarios[p.id] === undefined ? (
                            <button
                              type="button"
                              className="mt-1 text-[0.72rem] text-neutral-500 hover:text-neutral-900"
                              onClick={() => setComentarios({ ...comentarios, [p.id]: '' })}
                            >
                              + comentario
                            </button>
                          ) : (
                            <Textarea
                              rows={2}
                              autoFocus
                              value={comentarios[p.id]}
                              maxLength={280}
                              placeholder={`Algo puntual sobre ${p.nombre.toLowerCase()}…`}
                              className="mt-1.5 text-[0.82rem]"
                              onChange={(e) => setComentarios({ ...comentarios, [p.id]: e.target.value })}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end">
                      <Button type="submit">Guardar check-in de hoy</Button>
                    </div>
                  </form>

                  {checkins.length > 0 && (
                    <ul className="mt-4 flex list-none flex-col gap-1 p-0">
                      {[...checkins].reverse().map((c) => {
                        const coms = Object.entries(c.comentarios || {});
                        return (
                          <li key={c.fecha} className="border-b border-neutral-100 py-1.5 text-sm">
                            <div className="flex items-center justify-between gap-3">
                              <span className="tabular-nums text-neutral-600">{c.fecha}</span>
                              <span className="text-neutral-500">
                                promedio <strong className="text-neutral-900">{c.promedio ?? '—'}</strong>
                                {/* La escala se muestra sólo cuando NO es la
                                    actual: un "sobre 5" en cada renglón sería
                                    ruido, pero sin él un promedio de 3 sobre 5
                                    se lee como un 3 sobre 10, que es la mitad. */}
                                {c.escala && c.escala !== 10 && (
                                  <span className="text-[0.7rem] text-neutral-400"> sobre {c.escala}</span>
                                )}
                              </span>
                            </div>
                            {coms.length > 0 && (
                              <ul className="mt-1 flex list-none flex-col gap-0.5 p-0">
                                {coms.map(([id, texto]) => (
                                  <li key={id} className="text-[0.75rem] leading-snug text-neutral-500">
                                    <strong className="font-semibold text-neutral-700">
                                      {pilares.find((p) => p.id === id)?.nombre || id}:
                                    </strong>{' '}
                                    {texto}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </TabsContent>

                {/* ── Notas privadas ─────────────────────────────────────── */}
                <TabsContent value="notas" className="pt-3">
                  <p className="m-0 mb-2 text-sm text-neutral-600">
                    Sólo las ves vos. No salen por ninguna otra ruta ni las ve el asistente.
                  </p>
                  <div className="flex flex-col gap-2">
                    <Textarea
                      rows={3}
                      value={notaNueva}
                      onChange={(e) => setNotaNueva(e.target.value)}
                      placeholder="Qué se trabajó en la sesión, qué observar la próxima…"
                    />
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        disabled={!notaNueva.trim()}
                        onClick={async () => {
                          await guardarNota(currentUser, elegido.telefono, notaNueva.trim());
                          setNotaNueva('');
                          await cargarDelPaciente(elegido.telefono);
                        }}
                      >
                        Guardar nota
                      </Button>
                    </div>
                  </div>

                  <ul className="mt-4 flex list-none flex-col gap-2 p-0">
                    {notas.map((n) => (
                      <li key={n.id} className="rounded-lg border border-neutral-200 p-3">
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="text-[0.7rem] tabular-nums text-neutral-400">{n.fecha}</span>
                          <button
                            type="button"
                            className="text-[0.7rem] text-neutral-400 hover:text-red-600"
                            onClick={async () => {
                              await borrarNota(currentUser, elegido.telefono, n.id);
                              await cargarDelPaciente(elegido.telefono);
                            }}
                          >
                            Borrar
                          </button>
                        </div>
                        <p className="m-0 whitespace-pre-wrap text-sm text-neutral-700">{n.texto}</p>
                      </li>
                    ))}
                  </ul>
                </TabsContent>
              </Tabs>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
